import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sileo } from 'sileo';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { translateNotification } from '../../utils/notifications';

const SOCIAL_METHODS = {
  points_earned: 'success',
  referral_joined: 'info',
  reward_claimed: 'success',
  gift_delivered: 'success',
  gift_confirmed: 'success',
  pipeline_advanced: 'info',
  content_published: 'info',
};

const PERSONAL_METHODS = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

const TOAST_STYLES = {
  title: '!text-secondary-900',
  description: '!text-secondary-600',
  button: '!bg-orange-500 !text-white',
};

function localizeState(state, t) {
  if (!state) return state;
  const labels = t.admin || {};
  return labels[state] || state;
}

function socialText(payload, t, formatMessage) {
  const name = payload.actor?.display_name || t.socialActivity.fallbackActor;
  const params = { ...(payload.params || {}), name };
  if (params.state) params.state = localizeState(params.state, t);
  if (params.content_type) {
    params.contentType = t.socialActivity.contentTypes?.[params.content_type] || params.content_type;
  }
  const entry = t.socialActivity.events?.[payload.event_type];
  if (!entry) {
    return formatMessage(t.socialActivity.fallback, params);
  }
  return formatMessage(entry, params);
}

export default function ActivityToaster() {
  const { user } = useAuth();
  const { t, formatMessage } = useLanguage();
  const navigate = useNavigate();
  const seenRef = useRef(new Set());
  const activeIdsRef = useRef([]);

  const pushToast = useCallback((method, options) => {
    const fn = sileo[method] || sileo.show;
    const id = fn(options);
    activeIdsRef.current.push(id);
    while (activeIdsRef.current.length > 3) {
      sileo.dismiss(activeIdsRef.current.shift());
    }
    return id;
  }, []);

  const remember = useCallback((eventId) => {
    if (!eventId || seenRef.current.has(eventId)) return false;
    seenRef.current.add(eventId);
    if (seenRef.current.size > 250) {
      const first = seenRef.current.values().next().value;
      seenRef.current.delete(first);
    }
    return true;
  }, []);

  const showEvent = useCallback((payload) => {
    if (!payload || !remember(payload.event_id)) return;
    const createdAt = Date.parse(payload.created_at || '');
    if (!Number.isNaN(createdAt) && Date.now() - createdAt > 60_000) return;

    if (payload.kind === 'personal') {
      const notification = payload.notification || {};
      window.dispatchEvent(new CustomEvent('enrola:notification', { detail: notification }));
      const { title, message } = translateNotification(notification, t, formatMessage);
      const method = PERSONAL_METHODS[notification.type] || 'info';
      const actionPath = notification.action_path;
      const button = actionPath ? {
        title: t.notifications?.viewDetails || 'Ver',
        onClick: () => {
          if (notification.id) {
            fetch(`/api/notifications/${notification.id}/read`, {
              method: 'PUT',
              credentials: 'include',
            }).catch(() => {});
          }
          navigate(actionPath);
        },
      } : undefined;

      pushToast(method, {
        title: title || t.socialActivity.personalLabel,
        description: message || notification.message || '',
        duration: 5000,
        position: 'top-center',
        autopilot: { expand: 100, collapse: 5000 },
        roundness: 16,
        fill: '#ffffff',
        styles: TOAST_STYLES,
        button,
      });
      return;
    }

    if (payload.kind !== 'social' || !user?.is_client) return;

    pushToast(SOCIAL_METHODS[payload.event_type] || 'success', {
      title: t.socialActivity.socialLabel,
      description: socialText(payload, t, formatMessage),
      duration: 4500,
      position: 'top-center',
      autopilot: { expand: 100, collapse: 4500 },
      roundness: 16,
      fill: '#ffffff',
      styles: TOAST_STYLES,
    });
  }, [formatMessage, navigate, pushToast, remember, t, user?.is_client]);

  useEffect(() => {
    if (!user || typeof EventSource === 'undefined') return undefined;

    let source = null;
    let retryTimer = null;
    let retryDelay = 5000;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      retryTimer = null;
      source = new EventSource('/api/activity/stream', { withCredentials: true });

      source.addEventListener('activity', (event) => {
        try {
          showEvent(JSON.parse(event.data));
        } catch {
          return;
        }
      });

      source.onopen = () => {
        retryDelay = 5000;
      };

      source.onerror = () => {
        if (disposed || retryTimer) return;
        source.close();
        retryTimer = window.setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 60000);
      };
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      if (source) source.close();
    };
  }, [showEvent, user]);

  return null;
}
